import React, { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  AUTO_LOGOUT_DURATIONS,
  LAST_ACTIVITY_TIMESTAMP_KEY,
  MIN_LOGOUT_CHECK_INTERVAL,
  MAX_LOGOUT_CHECK_INTERVAL
} from '@/lib/auth/constants';

const AutoLogout: React.FC = () => {
  const { user, selectedLogoutDuration } = useAuth();

  // Refs (do not cause re-renders when updated)
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const logoutInProgressRef = useRef<boolean>(false);

  // Get configured duration in milliseconds
  const logoutDurationMs = AUTO_LOGOUT_DURATIONS[selectedLogoutDuration];

  // Calculate dynamic check interval: half the duration, clamped between min and max
  // This ensures shorter timeouts are checked frequently enough
  const checkInterval = Math.max(
    MIN_LOGOUT_CHECK_INTERVAL,
    Math.min(logoutDurationMs / 2, MAX_LOGOUT_CHECK_INTERVAL)
  );

  /**
   * Logout handler - ensures single execution even if called multiple times
   */
  const logout = useCallback(async () => {
    // Prevent multiple simultaneous logout attempts
    if (logoutInProgressRef.current) {
      return;
    }

    logoutInProgressRef.current = true;

    try {
      // Clear the activity timestamp from localStorage
      localStorage.removeItem(LAST_ACTIVITY_TIMESTAMP_KEY);

      // Sign out from Supabase
      await supabase.auth.signOut();
    } catch (error) {
      console.error('AutoLogout: Error during logout', error);
      // Reset flag so logout can be retried
      logoutInProgressRef.current = false;
    }
    // Note: Don't reset flag on success - user is logging out anyway
  }, []);

  /**
   * Update last activity timestamp in localStorage
   * Called on any user activity event
   */
  const updateActivityTimestamp = useCallback(() => {
    if (!user) {
      return; // Don't track activity if not logged in
    }

    const now = Date.now();
    try {
      localStorage.setItem(LAST_ACTIVITY_TIMESTAMP_KEY, now.toString());
    } catch (error) {
      console.error('AutoLogout: Failed to write timestamp to localStorage', error);
      // Fallback: Continue without localStorage (will reset on page reload)
    }
  }, [user]);

  /**
   * Check if enough time has elapsed since last activity
   * Called periodically by setInterval
   */
  const checkForLogout = useCallback(() => {
    // Don't check if user is not logged in
    if (!user) {
      return;
    }

    // Don't check if logout is already in progress
    if (logoutInProgressRef.current) {
      return;
    }

    // Get last activity timestamp from localStorage
    const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_TIMESTAMP_KEY);

    // If no timestamp exists, user just logged in - set current time
    if (!lastActivityStr) {
      updateActivityTimestamp();
      return;
    }

    const lastActivity = parseInt(lastActivityStr, 10);
    const now = Date.now();
    const elapsedTime = now - lastActivity;

    // Handle negative elapsed time (clock set backward)
    if (elapsedTime < 0) {
      console.warn('AutoLogout: Clock skew detected - resetting timestamp');
      updateActivityTimestamp();
      return;
    }

    // Check if elapsed time exceeds configured duration
    if (elapsedTime >= logoutDurationMs) {
      console.log(`AutoLogout: Inactive for ${elapsedTime}ms (threshold: ${logoutDurationMs}ms) - logging out`);
      logout();
    }
  }, [user, logoutDurationMs, logout, updateActivityTimestamp]);

  /**
   * Effect 1: Set up activity event listeners
   * Runs when component mounts or when user logs in/out
   */
  useEffect(() => {
    if (!user) {
      // User not logged in - clean up timestamp
      localStorage.removeItem(LAST_ACTIVITY_TIMESTAMP_KEY);
      return;
    }

    // Initialize timestamp when component mounts with logged-in user
    updateActivityTimestamp();

    // Activity events to track
    const activityEvents: (keyof WindowEventMap)[] = [
      'mousemove',
      'keydown',
      'scroll',
      'click',
      'touchstart'
    ];

    // Register all activity event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivityTimestamp);
    });

    // Cleanup: Remove event listeners when component unmounts or user logs out
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivityTimestamp);
      });
    };
  }, [user, updateActivityTimestamp]);

  /**
   * Effect 2: Set up periodic logout check
   * Runs when component mounts or when user/duration changes
   */
  useEffect(() => {
    if (!user) {
      // User not logged in - clear interval if it exists
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set up new interval to periodically check for logout
    intervalRef.current = setInterval(checkForLogout, checkInterval);

    // Run initial check immediately (don't wait for first interval)
    checkForLogout();

    // Cleanup: Clear interval when component unmounts or dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user, checkForLogout, checkInterval]);

  /**
   * Effect 3: Handle duration changes mid-session
   * When user changes logout duration in settings, reset the activity timestamp
   * so the new duration takes effect immediately
   */
  useEffect(() => {
    if (user) {
      // Reset activity timestamp so new duration starts counting from now
      updateActivityTimestamp();

      console.log(`AutoLogout: Duration changed to ${selectedLogoutDuration} (${logoutDurationMs}ms, checking every ${checkInterval}ms)`);
    }
  }, [selectedLogoutDuration, logoutDurationMs, checkInterval, user, updateActivityTimestamp]);

  // This component doesn't render anything
  return null;
};

export default AutoLogout;
